import {
    FC, useEffect, useMemo, useRef, useState,
} from 'react';

import type { LetterContent } from '@/types/recipientEmail';

import { buildCandidateDocument } from './candidateDocument';
import { observeCandidateFrame } from './candidateFrameSizing';
import styles from './recipient-email.module.scss';

const RecipientEmailRenderer: FC<Omit<LetterContent, 'subject'>> = ({ html, plainText, attachments }) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [wide, setWide] = useState(false);
    const [error, setError] = useState('');
    const [visibleFallbackText, setVisibleFallbackText] = useState<string | undefined>();
    const [failedImages, setFailedImages] = useState(0);
    const emailDocument = useMemo(
        () => buildCandidateDocument({ html, plainText, attachments }),
        [html, plainText, attachments],
    );
    const fallbackText = visibleFallbackText ?? emailDocument.fallbackText;

    useEffect(() => {
        const iframe = iframeRef.current;
        const container = containerRef.current;
        if (!iframe || !container) return undefined;
        setWide(false);
        setError('');
        setVisibleFallbackText(undefined);
        setFailedImages(0);
        return observeCandidateFrame(iframe, container, {
            onWide: setWide,
            onError: (message, text) => { setError(message); setVisibleFallbackText(text); },
            onReady: () => setError(''),
            documentId: emailDocument.documentId,
            onImageStatus: ({ failed }) => setFailedImages(failed),
        });
    }, [emailDocument.srcDoc, emailDocument.documentId]);

    return (
        <div
            className={styles.viewer}
            ref={containerRef}
            data-candidate-viewer
            data-fallback-mode={error ? 'text' : undefined}
            data-candidate-image-failures={failedImages}
        >
            {emailDocument.layoutWarnings.map((warning) => (
                <p className={styles.notice} role="status" key={warning}>{warning}</p>
            ))}
            {emailDocument.blockedResources > 0 && (
                <p className={styles.notice} role="status">
                    External images are blocked for privacy. Missing or unsupported images are marked as unavailable.
                </p>
            )}
            {failedImages > 0 && (
                <p className={styles.notice} role="status" data-candidate-image-warning>
                    {failedImages === 1 ? 'An embedded image is unavailable.' : 'Some embedded images are unavailable.'}
                    {' The message text remains available below.'}
                </p>
            )}
            {wide && (
                <p className={styles.notice} role="status">
                    This email contains a wide layout. Scroll horizontally inside the message to view every column.
                </p>
            )}
            {error && (
                <div role="alert" data-candidate-fallback>
                    <p>{error}</p>
                    <pre className={styles.fallback} dir="auto">
                        {fallbackText || 'This email has no readable message text.'}
                    </pre>
                </div>
            )}
            <iframe
                ref={iframeRef}
                className={styles.frame}
                title="Email message content"
                srcDoc={emailDocument.srcDoc}
                sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                referrerPolicy="no-referrer"
                data-candidate-email-frame
                hidden={Boolean(error)}
            />
        </div>
    );
};

export default RecipientEmailRenderer;
